import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Role, UserStatus } from '@sf/shared';
import { homePath, useAuth } from './authContext';
import { t } from '../lib/copy';

export function FullPageLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-dvh items-center justify-center text-sm text-slate"
    >
      <span
        className="mr-3 size-5 animate-spin rounded-full border-2 border-stone border-t-brand"
        aria-hidden
      />
      {t('loading.generic')}
    </div>
  );
}

/** Signed in, but no users/{uid} doc could be loaded. */
export function ProfileMissing() {
  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <div className="max-w-md rounded-card border border-stone bg-white p-6 text-center shadow-card">
        <h1 className="text-xl font-semibold">We couldn't load your account</h1>
        <p className="mt-2 text-sm text-slate">{t('error.generic')}</p>
        <button
          type="button"
          onClick={() => void import('../features/auth/api').then((m) => m.logout())}
          className="mt-4 h-10 rounded-input border border-stone px-4 text-sm font-medium hover:bg-mist"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/** Login/sign-up pages: bounce signed-in users to where they belong. */
export function PublicOnly({ children }: { children?: ReactNode }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (user && profile) {
    // Return to the page that sent them to login; the guards there re-check access.
    const from = (location.state as { from?: string } | null)?.from;
    const target = profile.status === 'active' && from ? from : homePath(profile);
    return <Navigate to={target} replace />;
  }
  return children ?? <Outlet />;
}

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children ?? <Outlet />;
}

/** Only active accounts get past this; pending/deactivated see their status screen. */
export function StatusGate({ children }: { children?: ReactNode }) {
  const { profile } = useAuth();
  if (!profile) return <ProfileMissing />;
  if (profile.status !== 'active') return <Navigate to={homePath(profile)} replace />;
  return children ?? <Outlet />;
}

export function RequireRole({ allow, children }: { allow: Role; children?: ReactNode }) {
  const { profile } = useAuth();
  if (!profile) return <ProfileMissing />;
  if (profile.role !== allow) return <Navigate to={homePath(profile)} replace />;
  return children ?? <Outlet />;
}

/** The pending/deactivated screens are only for accounts in that status. */
export function RequireStatus({ status, children }: { status: UserStatus; children?: ReactNode }) {
  const { profile } = useAuth();
  if (!profile) return <ProfileMissing />;
  if (profile.status !== status) return <Navigate to={homePath(profile)} replace />;
  return children ?? <Outlet />;
}
